import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { USER_URL } from '../config/environment';
const REFRESH_TOKEN_THRESHOLD = 60000;

const api = axios.create({
  baseURL: USER_URL
});

class TokenManager {
  constructor() {
    this.accessToken = localStorage.getItem('token');
    this.refreshToken = localStorage.getItem('refreshToken');
    this.tokenExpiryTime = null;
    this.refreshTimeout = null;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    
    if (this.accessToken) {
      this.updateTokenExpiryTime(this.accessToken);
    }

    this.startPeriodicValidation();
  }

  startPeriodicValidation() {

    setInterval(() => {
      if (this.accessToken) {
        const now = Date.now();
        if (this.tokenExpiryTime && now >= this.tokenExpiryTime) {
          this.logout();
        }
      }
    }, 60000); 
  }

  updateTokenExpiryTime(token) {
    try {
      const decoded = jwtDecode(token);
      this.tokenExpiryTime = decoded.exp * 1000;
      
     
      const now = Date.now();
      if (now >= this.tokenExpiryTime) {
        this.logout();
        return;
      }
      
      this.scheduleTokenRefresh();
    } catch (error) {
      console.error('Error decoding token:', error);
      this.logout();
    }
  }

  scheduleTokenRefresh() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    const now = Date.now();
    const timeUntilRefresh = this.tokenExpiryTime - now - REFRESH_TOKEN_THRESHOLD;

    if (timeUntilRefresh > 0) {
      this.refreshTimeout = setTimeout(() => this.refreshAccessToken(), timeUntilRefresh);
    } else {
      this.refreshAccessToken();
    }
  }

  async refreshAccessToken() {
    if (this.isRefreshing) {
      return new Promise((resolve) => {
        this.refreshSubscribers.push(resolve);
      });
    }

    this.isRefreshing = true;

    try {
      const email = localStorage.getItem('userEmail');
      const response = await api.post('/api/auth/refresh-token', {
        email,
        refreshToken: this.refreshToken
      });

      const { token, validTill } = response.data;
      
      this.accessToken = token;
      localStorage.setItem('token', token);
      
      this.updateTokenExpiryTime(token);

      this.refreshSubscribers.forEach(callback => callback(token));
      this.refreshSubscribers = [];

      return token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      if (error.response?.status === 401) {
        this.logout();
      }
      throw error;
    } finally {
      this.isRefreshing = false;
    }
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('user');
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
    window.location.href = '/';
  }

  getAccessToken() {
    return this.accessToken;
  }

  setTokens(accessToken, refreshToken) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    this.updateTokenExpiryTime(accessToken);
  }
}

export const tokenManager = new TokenManager();

api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    const isLoginRequest = originalRequest.url === '/api/auth/login';
    if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
      originalRequest._retry = true;

      try {
        const newToken = await tokenManager.refreshAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        tokenManager.logout();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api; 